import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { useAuth } from '../../contexts/AuthContext';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import InkPropertiesForm from './InkPropertiesForm';
import ConsumablePropertiesForm from './ConsumablePropertiesForm';
import PackagingPropertiesForm from './PackagingPropertiesForm';
import RawPropertiesForm from './RawPropertiesForm';
import UOMSelect from '../../components/Form/UOMSelect';
import LocationSelect from '../../components/Form/LocationSelect';
import { isAdmin as checkAdmin } from '../../utils/roleUtils';
import ItemSuppliersTab from './ItemSuppliersTab';
import ItemLotsTab from './ItemLotsTab';
import BackButton from '../../components/Common/BackButton';
import './Inventory.css';
import { formatAppDate } from '../../utils/dateUtils';
import { defaultLocationForItemType } from '../../utils/locations';

// Reorder levels of the item: one inventory_settings row per location, the strictest applies (as in the API)
const REORDER_LEVEL_FIELDS = ['safety_stock', 'reorder_point', 'maximum_stock'];
const getReorderLevels = (settings = []) => {
  const active = settings.filter(row => row.is_active !== 0 && row.is_active !== false);
  const values = (field) => active.map(row => Number(row[field] || 0));
  return {
    safety_stock: active.length ? Math.min(...values('safety_stock')) : 0,
    reorder_point: active.length ? Math.min(...values('reorder_point')) : 0,
    maximum_stock: active.length ? Math.max(...values('maximum_stock')) : 0
  };
};

const ViewInventoryItem = forwardRef(({ itemId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('procurement');
  const { t: tCommon } = useTranslation('common');
  const [item, setItem] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [typeProperties, setTypeProperties] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionTotal, setTransactionTotal] = useState(0);

  const { showSuccess, showError, showWarning } = useMessages();
  const { user } = useAuth();
  const isAdmin = checkAdmin(user);

  // Cost override modal state
  const [showCostOverride, setShowCostOverride] = useState(false);
  const [overrideData, setOverrideData] = useState({ new_average_cost: '', reason: '' });
  const [overrideLoading, setOverrideLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    fetchSuppliers();
    if (itemId && !createMode) {
      fetchMaterial();
    } else if (createMode) {
      const defaultType = 'RAW';
      const defaultProperties = getDefaultPropertiesForType(defaultType);
      const newMaterial = {
        item_code: '',
        item_name: '',
        item_type: defaultType,
        description: '',
        cost_per_unit: 0,
        average_cost: 0,
        unit_of_measure: 'PCS',
        shelf_life_days: null,
        storage_conditions: '',
        default_location: defaultLocationForItemType(defaultType),
        batch_tracking: false,
        quality_controlled: false,
        is_active: true,
        initial_stock_quantity: 0,
        initial_stock_location: '',
        ...defaultProperties
      };
      setItem(newMaterial);
      setEditData(newMaterial);
      // Start with general tab for new items
      setActiveTab('general');
    }
  }, [itemId, createMode]);

  // Fetch transactions when movements tab is activated
  useEffect(() => {
    if (activeTab === 'movements' && itemId && !createMode) {
      fetchTransactions();
    }
  }, [activeTab, itemId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchSuppliers = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/providers?page_size=100'));
      if (response.ok) {
        const data = await response.json();
        // API returns {providers: [...]}
        const suppliersData = data.providers || [];
        setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  const fetchTypeSpecificProperties = async (itemType, itemId) => {
    try {
      let endpoint = '';
      switch (itemType) {
        case 'INK':
          endpoint = 'ink-properties';
          break;
        case 'CONSUMABLE':
          endpoint = 'consumable-properties';
          break;
        case 'PACKAGING':
          endpoint = 'packaging-properties';
          break;
        case 'RAW':
          endpoint = 'raw-properties';
          break;
        default:
          return {};
      }

      const response = await apiFetch(getApiUrl(`/api/inventory/${itemId}/${endpoint}`));
      if (response.ok) {
        const properties = await response.json();
        return properties;
      }
    } catch (error) {
      console.error('Error fetching type-specific properties:', error);
    }
    return {};
  };

  const fetchMaterial = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory/${itemId}`));
      if (response.ok) {
        const data = await response.json();
        data.reorder_levels = getReorderLevels(data.inventory_settings);
        setItem(data);
        setEditData(data);

        // Fetch type-specific properties
        const properties = await fetchTypeSpecificProperties(data.item_type, itemId);
        setTypeProperties(properties);

        // Merge type properties into editData for the forms
        const mergedData = { ...data, ...properties };
        setEditData(mergedData);
      } else {
        setError(t('materials.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching item:', error);
      setError(t('materials.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page = 1) => {
    if (!itemId) return;

    setTransactionsLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory/${itemId}/transactions?page=${page}&page_size=20`));
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions);
        setTransactionTotal(data.total);
        setTransactionPage(page);
      } else {
        showError(t('materials.view.messages.transactionsFailed'));
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showError(t('materials.view.messages.transactionsError'));
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);

    // Handle item type changes
    if (field === 'item_type') {
      handleMaterialTypeChange(value, editData.item_type);
    }
  };

  const clearTypeSpecificData = () => {
    // Clear all type-specific properties from editData
    const baseFields = [
      'item_id', 'item_code', 'item_name', 'item_type', 'description',
      'cost_per_unit', 'unit_of_measure',
      'shelf_life_days', 'storage_conditions', 'default_location',
      'batch_tracking', 'quality_controlled', 'is_active', 'created_date', 'updated_date'
    ];

    setEditData(prev => {
      const cleaned = {};
      baseFields.forEach(field => {
        if (prev[field] !== undefined) {
          cleaned[field] = prev[field];
        }
      });
      return cleaned;
    });

    setTypeProperties({});
  };

  const getDefaultPropertiesForType = (itemType) => {
    switch (itemType) {
      case 'INK':
        return {
          ink_type: '',
          viscosity: null,
          opacity: null,
          drying_time_minutes: null,
          coverage_sqm_per_liter: null,
          pantone_match: ''
        };
      case 'CONSUMABLE':
        return {
          consumable_type: '',
          usage_rate_per_hour: null,
          replacement_frequency_hours: null,
          hazardous: false,
          msds_available: false
        };
      case 'PACKAGING':
        return {
          package_type: '',
          dimensions: '',
          capacity: null,
          weight: null,
          barrier_properties: '',
          closure_type: ''
        };
      case 'RAW':
        return {
          grade: '',
          thickness: null,
          width: null,
          length: null,
          roll_length_m: null,
          weight_per_sqm: null,
          tensile_strength: null,
          color_fastness: '',
          fire_rating: ''
        };
      default:
        return {};
    }
  };

  const handleMaterialTypeChange = (newType, oldType) => {
    // Clear existing type-specific data
    clearTypeSpecificData();

    // Add default properties for the new type
    const defaultProperties = getDefaultPropertiesForType(newType);
    setEditData(prev => ({
      ...prev,
      item_type: newType,
      // New item: the location follows the type (INK -> BT-001, ...) unless the user chose another one
      ...(createMode && (prev.default_location || '') === defaultLocationForItemType(oldType)
        ? { default_location: defaultLocationForItemType(newType) }
        : {}),
      ...defaultProperties
    }));

    // If user is currently on properties tab and switching to a type that doesn't have that tab,
    // switch them back to general tab
    if (activeTab === 'properties' && !['INK', 'CONSUMABLE', 'PACKAGING', 'RAW'].includes(newType)) {
      setActiveTab('general');
    }
  };

  const saveTypeSpecificProperties = async (itemType, itemId, editData) => {
    try {
      let endpoint = '';
      let properties = {};

      switch (itemType) {
        case 'INK':
          endpoint = 'ink-properties';
          properties = {
            ink_type: editData.ink_type,
            viscosity: editData.viscosity,
            opacity: editData.opacity,
            drying_time_minutes: editData.drying_time_minutes,
            coverage_sqm_per_liter: editData.coverage_sqm_per_liter,
            pantone_match: editData.pantone_match
          };
          break;
        case 'CONSUMABLE':
          endpoint = 'consumable-properties';
          properties = {
            consumable_type: editData.consumable_type,
            usage_rate_per_hour: editData.usage_rate_per_hour,
            replacement_frequency_hours: editData.replacement_frequency_hours,
            hazardous: editData.hazardous,
            msds_available: editData.msds_available
          };
          break;
        case 'PACKAGING':
          endpoint = 'packaging-properties';
          properties = {
            package_type: editData.package_type,
            dimensions: editData.dimensions,
            capacity: editData.capacity,
            weight: editData.weight,
            barrier_properties: editData.barrier_properties,
            closure_type: editData.closure_type
          };
          break;
        case 'RAW':
          endpoint = 'raw-properties';
          properties = {
            grade: editData.grade,
            thickness: editData.thickness,
            width: editData.width,
            length: editData.length,
            roll_length_m: editData.roll_length_m,
            weight_per_sqm: editData.weight_per_sqm,
            tensile_strength: editData.tensile_strength,
            color_fastness: editData.color_fastness,
            fire_rating: editData.fire_rating
          };
          break;
        default:
          return true; // No type-specific properties to save
      }

      // Filter out undefined/null values
      const cleanProperties = {};
      Object.keys(properties).forEach(key => {
        if (properties[key] !== undefined && properties[key] !== null && properties[key] !== '') {
          cleanProperties[key] = properties[key];
        }
      });

      if (Object.keys(cleanProperties).length === 0) {
        return true; // No properties to save
      }

      const response = await apiFetch(getApiUrl(`/api/inventory/${itemId}/${endpoint}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanProperties)
      });

      return response.ok;
    } catch (error) {
      console.error('Error saving type-specific properties:', error);
      return false;
    }
  };

  const handleSave = async () => {
    // The server needs a warehouse location for every new item (inventory settings and stock live per location)
    if (createMode && !editData.default_location && !editData.initial_stock_location) {
      showError(t('materials.view.messages.createFailed'), t('materials.view.messages.defaultLocationRequired'));
      return;
    }
    try {
      setError(null);
      const url = createMode
        ? getApiUrl('/api/inventory')
        : getApiUrl(`/api/inventory/${itemId}`);

      const method = createMode ? 'POST' : 'PUT';

      // Clean the data to only include item fields
      const cleanData = {
        item_type: editData.item_type,
        item_code: editData.item_code,
        item_name: editData.item_name,
        description: editData.description,
        cost_per_unit: parseFloat(editData.cost_per_unit) || 0,
        unit_of_measure: editData.unit_of_measure,
        shelf_life_days: editData.shelf_life_days ? parseInt(editData.shelf_life_days) : null,
        storage_conditions: editData.storage_conditions,
        default_location: editData.default_location,
        batch_tracking: Boolean(editData.batch_tracking),
        quality_controlled: Boolean(editData.quality_controlled),
        is_active: Boolean(editData.is_active)
      };

      // Include admin-only fields when creating as admin
      if (createMode && isAdmin) {
        cleanData.average_cost = parseFloat(editData.average_cost) || 0;
        if (editData.initial_stock_quantity && parseFloat(editData.initial_stock_quantity) > 0) {
          cleanData.initial_stock_quantity = parseFloat(editData.initial_stock_quantity);
          cleanData.initial_stock_location = editData.initial_stock_location || editData.default_location || undefined;
        }
      }

      // Reorder levels have their own endpoint; save them first so an invalid level stops the save
      const levels = editData.reorder_levels;
      if (!createMode && levels && REORDER_LEVEL_FIELDS.some(f => Number(levels[f] || 0) !== Number(item.reorder_levels?.[f] || 0))) {
        const levelsResponse = await apiFetch(getApiUrl(`/api/inventory/${itemId}/reorder-levels`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(Object.fromEntries(REORDER_LEVEL_FIELDS.map(f => [f, Number(levels[f] || 0)])))
        });
        if (!levelsResponse.ok) {
          await showApiError(levelsResponse, showError, tCommon, t('materials.view.messages.updateFailed'));
          return;
        }
      }

      // Remove undefined values
      Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === undefined) {
          delete cleanData[key];
        }
      });


      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanData)
      });

      if (response.ok) {
        const result = await response.json();
        const currentMaterialId = createMode ? result.item_id : itemId;

        // Save type-specific properties if any exist
        const propertiesSaved = await saveTypeSpecificProperties(
          cleanData.item_type,
          currentMaterialId,
          editData
        );

        if (!propertiesSaved) {
          showWarning(
            createMode ? t('materials.view.messages.createdTitle') : t('materials.view.messages.updatedTitle'),
            'Material was saved but some type-specific properties could not be updated.'
          );
        } else {
          showSuccess(
            createMode ? t('materials.view.messages.createdTitle') : t('materials.view.messages.updatedTitle'),
            createMode ? t('materials.view.messages.createdSuccess') : t('materials.view.messages.updatedSuccess')
          );
        }

        setHasChanges(false);

        if (createMode && result.item_id) {
          setItem(result);
          setEditData(result);
        } else if (!createMode) {
          await fetchMaterial();
        }

        if (onSave) onSave(result);
      } else {
        await showApiError(response, showError, tCommon, createMode ? t('materials.view.messages.createFailed') : t('materials.view.messages.updateFailed'));
        return;
      }
    } catch (error) {
      console.error('Error saving item:', error);
      setError(t('materials.view.messages.saveError'));
      showError(
        createMode ? t('materials.view.messages.createFailed') : t('materials.view.messages.updateFailed'),
        t('materials.view.messages.saveError')
      );
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(item);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('materials.view.messages.deleteConfirm'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/inventory/${itemId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(
          t('materials.view.messages.deletedTitle'),
          t('materials.view.messages.deletedSuccess')
        );
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon, t('materials.view.messages.deleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      showError(
        t('materials.view.messages.deleteFailed'),
        t('materials.view.messages.deleteError')
      );
    }
  };

  const handleCostOverride = async () => {
    if (!overrideData.reason || overrideData.reason.length < 5) return;
    setOverrideLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory/${itemId}/override-cost`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_average_cost: parseFloat(overrideData.new_average_cost) || 0,
          reason: overrideData.reason
        })
      });
      if (response.ok) {
        const result = await response.json();
        showSuccess(
          t('materials.view.costOverride.successTitle'),
          t('materials.view.costOverride.successMessage', {
            oldCost: parseFloat(result.old_average_cost).toFixed(4),
            newCost: parseFloat(result.new_average_cost).toFixed(4)
          })
        );
        setShowCostOverride(false);
        await fetchMaterial();
        if (activeTab === 'movements') await fetchTransactions();
      } else {
        await showApiError(response, showError, tCommon, t('materials.view.costOverride.failedTitle'));
      }
    } catch (error) {
      console.error('Error overriding cost:', error);
      showError(t('materials.view.costOverride.failedTitle'), 'Error overriding cost');
    } finally {
      setOverrideLoading(false);
    }
  };

  const renderGeneralTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('materials.view.sections.materialInfo')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('materials.view.fields.materialCode')} *</label>
            <input
              type="text"
              value={editData.item_code || ''}
              onChange={(e) => handleFieldChange('item_code', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('materials.view.fields.materialName')} *</label>
            <input
              type="text"
              value={editData.item_name || ''}
              onChange={(e) => handleFieldChange('item_name', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('materials.view.fields.type')} *</label>
            <select
              value={editData.item_type || 'RAW'}
              onChange={(e) => handleFieldChange('item_type', e.target.value)}
              disabled={!editMode && !createMode}
              required
            >
              <option value="RAW">{t('materials.types.raw')}</option>
              <option value="CONSUMABLE">{t('materials.types.consumable')}</option>
              <option value="PACKAGING">{t('materials.types.packaging')}</option>
              <option value="INK">{t('materials.types.ink')}</option>
              {/* Finished goods are created by the system from the product; they can be opened and edited here */}
              {(!createMode || editData.item_type === 'FINISHED_GOOD') && (
                <option value="FINISHED_GOOD">{t('materials.types.finishedGood')}</option>
              )}
            </select>
            {(editMode || createMode) && (
              <small className="field-hint">
                {t('materials.view.hints.typeProperties')}
              </small>
            )}
          </div>
          <div className="form-group">
            <label>{t('materials.view.fields.unitOfMeasure')}</label>
            <UOMSelect
              value={editData.unit_of_measure || 'PCS'}
              onChange={(value) => handleFieldChange('unit_of_measure', value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('materials.view.fields.costPerUnit')}</label>
            <input
              type="number"
              step="0.0001"
              value={editData.cost_per_unit || 0}
              onChange={(e) => handleFieldChange('cost_per_unit', parseFloat(e.target.value) || 0)}
              disabled={!editMode && !createMode}
            />
            <small className="field-hint">{t('materials.view.hints.standardCost')}</small>
          </div>
          <div className="form-group">
            <label>{t('materials.tableHeaders.avgCost')}</label>
            {createMode && isAdmin ? (
              <input
                type="number"
                step="0.0001"
                value={editData.average_cost || 0}
                onChange={(e) => handleFieldChange('average_cost', parseFloat(e.target.value) || 0)}
              />
            ) : (
              <input
                type="text"
                value={editData.average_cost && parseFloat(editData.average_cost) > 0
                  ? `$${parseFloat(editData.average_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
                  : '-'}
                disabled={true}
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            )}
            <small className="field-hint">
              {createMode && isAdmin
                ? t('materials.view.hints.averageCostSeed')
                : t('materials.view.hints.averageCost')}
            </small>
            {!createMode && !editMode && isAdmin && (
              <button
                className="btn-secondary"
                style={{ alignSelf: 'flex-start', padding: '4px 10px', fontSize: '0.8em' }}
                onClick={() => {
                  setOverrideData({ new_average_cost: editData.average_cost || 0, reason: '' });
                  setShowCostOverride(true);
                }}
              >
                {t('materials.view.costOverride.overrideButton')}
              </button>
            )}
          </div>
          <div className="form-group">
            <label>{t('materials.view.fields.lastPurchaseCost')}</label>
            <input
              type="text"
              value={editData.last_purchase_cost && parseFloat(editData.last_purchase_cost) > 0
                ? `$${parseFloat(editData.last_purchase_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
                : '-'}
              disabled={true}
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            <small className="field-hint">
              {editData.last_purchase_date
                ? `${t('materials.view.hints.lastPurchase')} ${formatAppDate(editData.last_purchase_date)}`
                : t('materials.view.hints.noPurchaseHistory')}
            </small>
          </div>
          <div className="form-group">
            <label>{t('materials.view.supplierInfo.defaultLocation')}{createMode && ' *'}</label>
            <LocationSelect
              value={editData.default_location || ''}
              onChange={(value) => handleFieldChange('default_location', value)}
              disabled={!editMode && !createMode}
            />
          </div>
        </div>
      </div>

      {createMode && isAdmin && (
        <div className="form-section">
          <h3>{t('materials.view.openingBalance.title')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('materials.view.openingBalance.initialQuantity')}</label>
              <input
                type="number"
                step="0.001"
                value={editData.initial_stock_quantity || 0}
                onChange={(e) => handleFieldChange('initial_stock_quantity', parseFloat(e.target.value) || 0)}
              />
              <small className="field-hint">{t('materials.view.openingBalance.quantityHint')}</small>
            </div>
            <div className="form-group">
              <label>{t('materials.view.openingBalance.initialLocation')}</label>
              <LocationSelect
                value={editData.initial_stock_location || editData.default_location || ''}
                onChange={(value) => handleFieldChange('initial_stock_location', value)}
              />
              <small className="field-hint">{t('materials.view.openingBalance.locationHint')}</small>
            </div>
          </div>
        </div>
      )}

      <div className="form-section">
        <h3>{t('materials.view.description.title')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('materials.view.fields.description')}</label>
            <textarea
              value={editData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('materials.view.description.placeholder')}
              rows="3"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStockTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('materials.view.storageHandling.title')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('materials.view.storageHandling.fields.shelfLife')}</label>
            <input
              type="number"
              value={editData.shelf_life_days || ''}
              onChange={(e) => handleFieldChange('shelf_life_days', e.target.value ? parseInt(e.target.value) : null)}
              disabled={!editMode && !createMode}
              placeholder={t('materials.view.storageHandling.placeholders.shelfLifeDays')}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('materials.view.storageHandling.materialProperties')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('materials.view.storageHandling.fields.storageConditions')}</label>
            <textarea
              value={editData.storage_conditions || ''}
              onChange={(e) => handleFieldChange('storage_conditions', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('materials.view.storageHandling.placeholders.storageConditions')}
              rows="2"
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editData.batch_tracking || false}
                onChange={(e) => handleFieldChange('batch_tracking', e.target.checked)}
                disabled={!editMode && !createMode}
              />
              <span>{t('materials.view.storageHandling.fields.batchTracking')}</span>
            </label>
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editData.quality_controlled || false}
                onChange={(e) => handleFieldChange('quality_controlled', e.target.checked)}
                disabled={!editMode && !createMode}
              />
              <span>{t('materials.view.storageHandling.fields.qualityControlled')}</span>
            </label>
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editData.is_active !== false}
                onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                disabled={!editMode && !createMode}
              />
              <span>{t('materials.view.storageHandling.fields.active')}</span>
            </label>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('materials.view.reorderLevels.title')}</h3>
        {createMode ? (
          <small className="field-hint">{t('materials.view.reorderLevels.afterCreate')}</small>
        ) : (
          <>
            <div className="form-grid">
              {REORDER_LEVEL_FIELDS.map(field => (
                <div className="form-group" key={field}>
                  <label>{t(`materials.view.reorderLevels.fields.${field}`)}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={editData.reorder_levels?.[field] ?? 0}
                    onChange={(e) => handleFieldChange('reorder_levels', {
                      ...editData.reorder_levels,
                      [field]: e.target.value === '' ? 0 : Math.max(0, parseFloat(e.target.value) || 0)
                    })}
                    disabled={!editMode}
                  />
                </div>
              ))}
            </div>
            <small className="field-hint">{t('materials.view.reorderLevels.hint')}</small>
          </>
        )}
      </div>
    </div>
  );

  const renderPropertiesTab = () => {
    if (!editData.item_type) return null;

    const itemType = editData.item_type;
    const disabled = !editMode && !createMode;

    return (
      <div className="tab-content">
        {itemType === 'INK' && (
          <InkPropertiesForm
            editData={editData}
            onFieldChange={handleFieldChange}
            disabled={disabled}
          />
        )}
        {itemType === 'CONSUMABLE' && (
          <ConsumablePropertiesForm
            editData={editData}
            onFieldChange={handleFieldChange}
            disabled={disabled}
          />
        )}
        {itemType === 'PACKAGING' && (
          <PackagingPropertiesForm
            editData={editData}
            onFieldChange={handleFieldChange}
            disabled={disabled}
          />
        )}
        {itemType === 'RAW' && (
          <RawPropertiesForm
            editData={editData}
            onFieldChange={handleFieldChange}
            disabled={disabled}
            unitOfMeasure={editData.unit_of_measure}
          />
        )}
      </div>
    );
  };

  const formatTransactionType = (type, subtype) => {
    const typeColors = {
      'IN': 'status-success',
      'OUT': 'status-error',
      'ADJUST': 'status-warning',
      'TRANSFER': 'status-info',
      'RETURN': 'status-success',
      'WASTE': 'status-error'
    };

    return (
      <span className={`transaction-type ${typeColors[type] || ''}`}>
        {type} {subtype && `(${subtype})`}
      </span>
    );
  };

  const formatQuantity = (quantity, type) => {
    const prefix = type === 'IN' || type === 'RETURN' ? '+' : type === 'OUT' || type === 'WASTE' ? '-' : '';
    return `${prefix}${parseFloat(quantity).toLocaleString()}`;
  };

  const renderMovementsTab = () => {
    if (createMode) {
      return (
        <div className="tab-content">
          <div className="form-section">
            <h3>{t('materials.view.movements.title')}</h3>
            <p className="info-message">{t('materials.view.movements.availableAfterCreation')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="tab-content">
        <div className="form-section">
          <h3>{t('materials.view.movements.title')}</h3>
          {transactionsLoading ? (
            <div className="loading">{t('materials.view.movements.loadingMovements')}</div>
          ) : transactions.length > 0 ? (
            <div className="transactions-table">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('materials.view.movements.tableHeaders.dateTime')}</th>
                    <th>{t('materials.view.movements.tableHeaders.type')}</th>
                    <th>{t('materials.view.movements.tableHeaders.quantity')}</th>
                    <th style={{ textAlign: 'right' }}>{t('materials.view.movements.tableHeaders.unitCost')}</th>
                    <th style={{ textAlign: 'right' }}>{t('materials.view.movements.tableHeaders.avgCost')}</th>
                    <th>{t('materials.view.movements.tableHeaders.balanceAfter')}</th>
                    <th>{t('materials.view.movements.tableHeaders.location')}</th>
                    <th>{t('materials.view.movements.tableHeaders.reason')}</th>
                    <th>{t('materials.view.movements.tableHeaders.performedBy')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction.transaction_id}>
                      <td>
                        {formatAppDate(transaction.transaction_date, 'datetime')}
                      </td>
                      <td>
                        {formatTransactionType(transaction.transaction_type, transaction.transaction_subtype)}
                      </td>
                      <td className={`quantity ${transaction.transaction_type === 'IN' || transaction.transaction_type === 'RETURN' ? 'positive' : 'negative'}`}>
                        {formatQuantity(transaction.quantity, transaction.transaction_type)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {transaction.unit_cost && parseFloat(transaction.unit_cost) > 0
                          ? `$${parseFloat(transaction.unit_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
                          : '-'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {transaction.running_average_cost != null
                          ? `$${parseFloat(transaction.running_average_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
                          : '-'}
                      </td>
                      <td>{parseFloat(transaction.balance_after || 0).toLocaleString()}</td>
                      <td>
                        {transaction.location_from && transaction.location_to ?
                          `${transaction.location_from} → ${transaction.location_to}` :
                          transaction.location_from || transaction.location_to || '-'
                        }
                      </td>
                      <td>{transaction.reason || '-'}</td>
                      <td>{transaction.performed_by_name || tCommon('ui.unknown')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {transactionTotal > 20 && (
                <div className="pagination-info">
                  {t('materials.view.movements.pagination.showing', { count: transactions.length, total: transactionTotal })}
                  {/* Add pagination controls here if needed */}
                </div>
              )}
            </div>
          ) : (
            <div className="no-data">
              <p>{t('materials.view.movements.noMovements')}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('materials.view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!item && !createMode) {
    return <div className="view-container"><div className="loading">{t('materials.view.titles.noMaterialFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <BackButton onBack={onBack} entityType="inventory-item" entityIds={[itemId, item?.item_id]} />
          <h1>
            {createMode ? t('materials.view.titles.newMaterial') : editData.item_name || t('materials.view.titles.viewMaterial')}
          </h1>
          {!createMode && editData.item_type && (
            <span className={`type-badge type-${editData.item_type.toLowerCase()}`}>
              {editData.item_type.replace('_', ' ')}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{t('materials.toolbar.edit')}</button>
              {isAdmin && (
                <button className="btn-danger" onClick={handleDelete}>{t('materials.toolbar.delete')}</button>
              )}
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('materials.toolbar.cancel')}</button>
              <button className="btn-primary" onClick={handleSave}>
                {createMode ? t('materials.toolbar.create') : t('materials.toolbar.save')}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('materials.view.tabs.general')}
          </button>
          <button
            className={`tab-item ${activeTab === 'stock' ? 'active' : ''}`}
            onClick={() => setActiveTab('stock')}
          >
            {t('materials.view.tabs.storage')}
          </button>
          {editData.item_type && ['INK', 'CONSUMABLE', 'PACKAGING', 'RAW'].includes(editData.item_type) && (
            <button
              className={`tab-item ${activeTab === 'properties' ? 'active' : ''}`}
              onClick={() => setActiveTab('properties')}
            >
              {editData.item_type === 'INK' && t('materials.view.tabs.inkProperties')}
              {editData.item_type === 'CONSUMABLE' && t('materials.view.tabs.consumableProperties')}
              {editData.item_type === 'PACKAGING' && t('materials.view.tabs.packagingProperties')}
              {editData.item_type === 'RAW' && t('materials.view.tabs.rawProperties')}
            </button>
          )}
          <button
            className={`tab-item ${activeTab === 'suppliers' ? 'active' : ''}`}
            onClick={() => setActiveTab('suppliers')}
          >
            {t('materials.view.tabs.suppliers', 'Suppliers')}
          </button>
          {!!editData.batch_tracking && !createMode && (
            <button
              className={`tab-item ${activeTab === 'lots' ? 'active' : ''}`}
              onClick={() => setActiveTab('lots')}
            >
              {t('materials.view.tabs.lots', 'Lots')}
            </button>
          )}
          <button
            className={`tab-item ${activeTab === 'movements' ? 'active' : ''}`}
            onClick={() => setActiveTab('movements')}
          >
            {t('materials.view.tabs.movements')}
          </button>
        </div>

        <div className="tab-panels">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'stock' && renderStockTab()}
          {activeTab === 'properties' && renderPropertiesTab()}
          {activeTab === 'suppliers' && <ItemSuppliersTab itemId={itemId} editMode={editMode} createMode={createMode} />}
          {activeTab === 'lots' && <ItemLotsTab itemId={itemId} />}
          {activeTab === 'movements' && renderMovementsTab()}
        </div>
      </div>

      {showCostOverride && (
        <div className="modal-overlay" onClick={() => setShowCostOverride(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>{t('materials.view.costOverride.title')}</h2>
              <button className="modal-close" onClick={() => setShowCostOverride(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#666' }}>{t('materials.view.costOverride.description')}</p>
              <div className="form-group">
                <label>{t('materials.view.costOverride.currentCost')}</label>
                <input
                  type="text"
                  value={editData.average_cost && parseFloat(editData.average_cost) > 0
                    ? `$${parseFloat(editData.average_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
                    : '$0.00'}
                  disabled={true}
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-group">
                <label>{t('materials.view.costOverride.newCost')}</label>
                <input
                  type="number"
                  step="0.0001"
                  value={overrideData.new_average_cost}
                  onChange={(e) => setOverrideData(prev => ({ ...prev, new_average_cost: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>{t('materials.view.costOverride.reason')}</label>
                <textarea
                  value={overrideData.reason}
                  onChange={(e) => setOverrideData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder={t('materials.view.costOverride.reasonPlaceholder')}
                  rows="3"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setShowCostOverride(false)}>
                {t('materials.view.costOverride.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleCostOverride}
                disabled={overrideLoading || !overrideData.reason || overrideData.reason.length < 5}
              >
                {overrideLoading ? '...' : t('materials.view.costOverride.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ViewInventoryItem.displayName = 'ViewInventoryItem';

export default ViewInventoryItem;